import { Config } from '../config';
import { Auth } from './auth.service';
import { Consumer } from './types/directBanking/consumer.type';
import { OrderListItem } from './types/directBanking/orderListItem.type';
import { DirectBankingOrderDetails } from './types/directBanking/OrderDetails.type';
import { BatchListItem } from './types/directBanking/batchListItem.type';
import { RefundListItem } from './types/directBanking/refundListItem.type';
import { BatchDetails } from './types/directBanking/batchDetails.type';
import { Attachment } from './types/directBanking/attachment.type';

export class DirectBankingMiddlewareApi {

  constructor(
    private readonly config: Config,
    private readonly auth: Auth,
  ) { }

  async listDirectBankingConsumers(): Promise<Consumer[]> {
    const response = await fetch(
      this.config.DIRECTBANKING_MIDDLEWARE_API_URL + '/admin/consumers?external_only=true',
      {
        method: 'GET',
        headers: {
          Authorization: 'Bearer ' + await this.auth.getAccessToken()
        }
      }
    );
    if (!response.ok) {
      throw new Error('Could not fetch direct banking consumers')
    };
    return (await response.json()).consumers;
  }

  async listDirectBankingOrders(
    filters: {
      consumerId?: number;
      fromDate?: Date;
      toDate?: Date;
      status?: string;
      externalReference?: string;
      hasRefunds?: boolean,
      paid: boolean
    },
    limit: number,
    offset: number,
    order: 'asc' | 'desc' = 'desc',
    isAdmin: boolean = false
  ): Promise<{ count: number; orders: OrderListItem[] }> {
    const params = new URLSearchParams();
    params.append('limit', limit.toString(10));
    params.append('offset', offset.toString(10));
    params.append('order', order);
    params.append('only_consumers_with_location', 'true')

    if (filters.consumerId) {
      params.append('consumer_id', filters.consumerId.toString(10));
    }
    if (filters.status) {
      params.append('status', filters.status);
    }
    if (filters.externalReference) {
      params.append('external_reference', filters.externalReference);
    }
    if (filters.paid === true) {
      params.append('paid', filters.paid.toString());
    }
    if (filters.hasRefunds === true) {
      params.append('has_refunds', filters.hasRefunds.toString());
    }


    const url = (isAdmin ? '/admin/orders?' : '/orders?') + params.toString();

    const response = await fetch(
      this.config.DIRECTBANKING_MIDDLEWARE_API_URL + url,
      {
        method: 'GET',
        headers: {
          Authorization: 'Bearer ' + await this.auth.getAccessToken()
        }
      }
    )
    if (!response.ok) {
      throw new Error('Could not fetch direct banking orders');
    }
    const result = await response.json()
    return result
  }

  async getDirectBankingOrderDetails(id: string | number, isAdmin: boolean = false): Promise<DirectBankingOrderDetails> {
    if (typeof id === 'number') {
      id = id.toString(10);
    }

    let url = '/admin/orders/' + id;
    if (!isAdmin) {
      url = '/orders/' + id;
    }

    const response = await fetch(
      this.config.DIRECTBANKING_MIDDLEWARE_API_URL + url,
      {
        method: 'GET',
        headers: {
          Authorization: 'Bearer ' + await this.auth.getAccessToken()
        }
      }
    )
    if (!response.ok) {
      throw new Error('Could not fetch direct banking order details');
    }
    const jsonResponse = await response.json();
    return jsonResponse;
  }

  async issueDirectBankingRefund(orderId: number, amount: number, isAdmin: boolean = false): Promise<void> {
    const payload: any = {
      amount: amount
    }

    let url = '/admin/orders/' + orderId + '/refund';
    if (!isAdmin) {
      url = '/orders/' + orderId + '/refund';
    }

    const response = await fetch(
      this.config.DIRECTBANKING_MIDDLEWARE_API_URL + url,
      {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          Authorization: 'Bearer ' + await this.auth.getAccessToken()
        },
        body: JSON.stringify(payload)
      }
    )
    if (!response.ok) {
      throw new Error('Could not create the refund');
    }
  }

  async changeOrderExceptionStatus(orderId: number, status: string) {
    const payload: any = {
      status: status
    }
    const response = await fetch(
      this.config.DIRECTBANKING_MIDDLEWARE_API_URL + '/admin/orders/' + orderId + '/exception',
      {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          Authorization: 'Bearer ' + await this.auth.getAccessToken()
        },
        body: JSON.stringify(payload)
      }
    )
    if (!response.ok) {
      throw new Error('Could not mark the order as exception');
    }
  }

  async listDirectBankingBatches(
    filters: {
      consumerId?: number;
      start?: Date;
      end?: Date;
    },
    limit: number,
    offset: number,
    order: 'asc' | 'desc' = 'desc',
  ): Promise<{ count: number; batches: BatchListItem[] }> {
    const params = new URLSearchParams();
    params.append('limit', limit.toString(10));
    params.append('offset', offset.toString(10));
    params.append('order', order);
    if (filters.consumerId) {
      params.append('consumer_id', filters.consumerId.toString(10));
    }
    if (filters.start) {
      params.append('start', filters.start.toISOString());
    }
    if (filters.end) {
      params.append('end', filters.end.toISOString());
    }

    const response = await fetch(
      this.config.DIRECTBANKING_MIDDLEWARE_API_URL + '/admin/batches?' + params.toString(),
      {
        method: 'GET',
        headers: {
          Authorization: 'Bearer ' + await this.auth.getAccessToken()
        }
      }
    )
    if (!response.ok) {
      throw new Error('Could not fetch direct banking batches');
    }
    const result = await response.json();
    return result;
  }

  async listDirectBankingRefunds(
    filters: {
      consumerId?: number;
      status?: 'CREATED' | 'PROCESSED';
    }
  ): Promise<RefundListItem[]> {
    const params = new URLSearchParams();
    if (filters.consumerId) {
      params.append('consumer_id', filters.consumerId.toString(10));
    }
    if (filters.status) {
      params.append('status', filters.status);
    }
    const response = await fetch(
      this.config.DIRECTBANKING_MIDDLEWARE_API_URL + '/admin/refunds?' + params.toString(),
      {
        method: 'GET',
        headers: {
          Authorization: 'Bearer ' + await this.auth.getAccessToken()
        }
      }
    )
    if (!response.ok) {
      throw new Error('Could not fetch direct banking refunds');
    }
    const result = await response.json();
    return result.refunds;
  }

  async newOrder(
    externalReference: string,
    amount: number,
    urlOk: string,
    urlKo: string,
    currencyIsoCode3: string,
    urlCallback: string,
    locale: string
  ): Promise<{payment_url: string, order_id: number}> {
    const response = await fetch(
      `${this.config.DIRECTBANKING_MIDDLEWARE_API_URL}/orders`,
      {
        method: 'POST',
        headers: {
          Authorization: 'Bearer ' + await this.auth.getAccessToken(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          external_reference: externalReference,
          amount: amount,
          url_ok: urlOk,
          url_ko: urlKo,
          currency_iso3_code: currencyIsoCode3,
          url_callback: urlCallback,
          locale: locale
        })
      }
    )
    if (!response.ok) {
      throw new Error('Could not create order');
    } else {
      return (await response.json());
    }
  }

  async getDirectBankingBatchDetails(id: string | number): Promise<BatchDetails> {
    if (typeof id === 'number') {
      id = id.toString(10);
    }
    const response = await fetch(
      this.config.DIRECTBANKING_MIDDLEWARE_API_URL + '/admin/batches/' + id,
      {
        method: 'GET',
        headers: {
          Authorization: 'Bearer ' + await this.auth.getAccessToken()
        }
      }
    )
    if (!response.ok) {
      throw new Error('Could not fetch directbanking batch details');
    }
    return response.json();
  }

  async getDirectBankingBatchUploadUrlForAttachment(id: string | number, name: string, type: string): Promise<string> {
    if (typeof id === 'number') {
      id = id.toString(10);
    }
    const response = await fetch(
      this.config.DIRECTBANKING_MIDDLEWARE_API_URL + '/admin/batches/' + id + '/attachments',
      {
        method: 'POST',
        headers: {
          Authorization: 'Bearer ' + await this.auth.getAccessToken(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name, content_type: type
        })
      }
    )
    if (!response.ok) {
      throw new Error('Could not fetch directbanking batch attachment upload url');
    }
    return (await response.json()).url;
  }

  async getDirectBankingOrderUploadUrlForAttachment(id: string | number, name: string, type: string): Promise<string> {
    if (typeof id === 'number') {
      id = id.toString(10);
    }
    const response = await fetch(
      this.config.DIRECTBANKING_MIDDLEWARE_API_URL + '/admin/orders/' + id + '/attachments',
      {
        method: 'POST',
        headers: {
          Authorization: 'Bearer ' + await this.auth.getAccessToken(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name, content_type: type
        })
      }
    )
    if (!response.ok) {
      throw new Error('Could not fetch directbanking order attachment upload url');
    }
    return (await response.json()).url;
  }

  async getDirectBankingBatchAttachments(id: string | number): Promise<Attachment[]> {
    if (typeof id === 'number') {
      id = id.toString(10);
    }
    const response = await fetch(
      this.config.DIRECTBANKING_MIDDLEWARE_API_URL + '/admin/batches/' + id + '/attachments',
      {
        method: 'GET',
        headers: {
          Authorization: 'Bearer ' + await this.auth.getAccessToken(),
        }
      }
    )
    if (!response.ok) {
      throw new Error('Could not fetch directbanking batch attachments');
    }
    return (await response.json()).attachments;
  }

  async getDirectBankingOrderAttachments(id: string | number): Promise<Attachment[]> {
    if (typeof id === 'number') {
      id = id.toString(10);
    }
    const response = await fetch(
      this.config.DIRECTBANKING_MIDDLEWARE_API_URL + '/admin/orders/' + id + '/attachments',
      {
        method: 'GET',
        headers: {
          Authorization: 'Bearer ' + await this.auth.getAccessToken(),
        }
      }
    )
    if (!response.ok) {
      throw new Error('Could not fetch directbanking order attachments');
    }
    return (await response.json()).attachments;
  }

  async getOrdersReport(start: string, end: string, isAdmin: boolean): Promise<{ file_url: string }> {
    const queryParams = new URLSearchParams();
    queryParams.append('start', start);
    queryParams.append('end', end);

    let url = `/admin/reports/orders?` + queryParams.toString();
    if (!isAdmin) {
      url = `/reports/orders?` + queryParams.toString();
    }

    const response = await fetch(
      this.config.DIRECTBANKING_MIDDLEWARE_API_URL + url,
      {
        method: 'GET',
        headers: {
          Authorization: 'Bearer ' + await this.auth.getAccessToken()
        },
      }
    )
    if (!response.ok) {
      throw new Error('Could not build report');
    }
    return response.json();
  }

  async getFees(isAdmin: boolean): Promise<
    {
      consumer: { id: number, name: string, prefunded_fee: boolean },
      fees: { id: number, currency_iso_code_3: string, min_amount: number, max_amount: number, cost_fixed_amount: number }[]
    }[]
  > {

    let url = '/admin/fees';
    if (!isAdmin) {
      url = '/fees';
    }

    const response = await fetch(
      this.config.DIRECTBANKING_MIDDLEWARE_API_URL + url,
      {
        method: 'GET',
        headers: {
          Authorization: 'Bearer ' + await this.auth.getAccessToken()
        },
      }
    )
    if (!response.ok) {
      throw new Error('Could not build report');
    }
    const fees = await response.json();
    const grouped = fees.fees.reduce((acc: any, item: any) => {
      const consumerId = item.consumer.id;
      if (!acc[consumerId]) {
        acc[consumerId] = {
          consumer: { id: item.consumer.id, name: item.consumer.name, prefunded_fee: item.consumer.prefunded_fee },
          fees: []
        };
      }
      acc[consumerId].fees.push({
        id: item.fee.id,
        currency_iso_code_3: item.fee.currency_iso_code_3,
        min_amount: item.fee.min_amount,
        max_amount: item.fee.max_amount,
        cost_fixed_amount: item.fee.cost_fixed_amount
      });
      return acc;
    }, {} as Record<number, { consumer: { id: number, name: string, prefunded_fee: boolean }, fees: { id: number, currency_iso_code_3: string, min_amount: number, max_amount: number, cost_fixed_amount: number }[] }>);

    const result = Object.values(grouped).filter(g => g.consumer.prefunded_fee);

    return result as {
      consumer: { id: number, name: string, prefunded_fee: boolean },
      fees: { id: number, currency_iso_code_3: string, min_amount: number, max_amount: number, cost_fixed_amount: number }[]
    }[];
  }

    async completeClearing(
    transactionId: number
  ) {
    const response = await fetch(
      `${this.config.DIRECTBANKING_MIDDLEWARE_API_URL}/admin/orders/${transactionId}/clearing/complete`,
      {
        method: 'POST',
        headers: {
          Authorization: 'Bearer ' + await this.auth.getAccessToken()
        }
      }
    )
    if (!response.ok) {
      if (response.status === 405) {
        throw new Error('405');
      }
      throw new Error('Error in clearing');
    }
  }



}